import { keyBy, partition } from 'es-toolkit/array';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  MAX_UPLOADED_MEDIA_COUNT,
  questionnaireImageFileConfiguration,
  questionnaireVideoFileConfiguration,
} from '../../configurations';
import {
  AwardedNomination,
  EventAttendee,
  Questionnaire,
  QuestionnaireProgress,
  UploadedMedia,
  WinnerContact,
  WinnerEvent,
  blankEventAttendee,
  blankQuestionnaire,
  copyQuestionnaire,
  copyQuestionnaireProgress,
} from '../../models';
import { AwardedNominationRole } from '../../models/shared/winner-role-types';
import {
  EventAttendeeValidation,
  QuestionnaireContactValidation,
  QuestionnaireMediaValidation,
  QuestionnaireWizardValidationInput,
  eventAttendeeValidationSchema,
  foodEventAttendeeValidationSchema,
  questionnaireContactValidationSchema,
  questionnaireMediaValidationSchema,
} from '../../models/schemas/questionnaire-wizard-validation.schema';
import { createValidationState, VALID_VALIDATION_STATE, ValidationState } from '../../shared/validation';
import { isEmpty } from 'es-toolkit/compat';
import {
  getVisibleQuestionnaireStepIds,
  QuestionnaireStepId,
} from './questionnaire-stepper';

export type ValidatedSteps = Partial<Record<QuestionnaireStepId, boolean>>;

export type QuestionnaireMediaType = 'image' | 'video';

const mediaFileExtensions: Record<QuestionnaireMediaType, string[]> = {
  image: questionnaireImageFileConfiguration.supportedFileExtensions,
  video: questionnaireVideoFileConfiguration.supportedFileExtensions,
};

export class QuestionnaireWizardPageUiStore {
  @observable furthestVisitedStepId: QuestionnaireStepId = 'contact';

  @observable initialStepId: QuestionnaireStepId = 'contact';

  @observable validatedSteps: ValidatedSteps = {};

  @observable questionnaire: Questionnaire = blankQuestionnaire();

  @observable winnerEvents: WinnerEvent[] = [];

  @observable awardedNominations: AwardedNomination[] = [];

  @observable questionnaireProgress?: QuestionnaireProgress;

  @observable deletedEventAttendeeIds: number[] = [];

  constructor() {
    makeObservable(this);
  }

  @computed
  get isSubmitted(): boolean {
    return this.questionnaire.status === 'Submitted';
  }

  @computed
  get hasWinnerEvents(): boolean {
    return this.winnerEvents.length > 0;
  }

  @computed
  get visibleStepIds(): QuestionnaireStepId[] {
    return getVisibleQuestionnaireStepIds(this.hasWinnerEvents, this.winnerRole);
  }

  @computed
  get awardedNomination(): AwardedNomination | undefined {
    return this.awardedNominations.find(
      (awardedNomination) => awardedNomination.winnerId === this.questionnaire.winnerId,
    );
  }

  @computed
  get winnerRole(): AwardedNominationRole | undefined {
    return this.awardedNomination?.role;
  }

  @computed
  get questionnaireId(): number {
    return this.questionnaire.questionnaireId;
  }

  @computed
  get isMediaUploadLimitReached(): boolean {
    return this.questionnaire.media.length >= MAX_UPLOADED_MEDIA_COUNT;
  }

  getValidationInput(): QuestionnaireWizardValidationInput {
    const winnerEventsById = keyBy(this.winnerEvents, (winnerEvent) => winnerEvent.eventId);
    const visibleAttendingEventAttendees = this.questionnaire.eventAttendees.filter(
      (eventAttendee) => eventAttendee.isAttending && winnerEventsById[eventAttendee.eventId] !== undefined,
    );
    const [attendingFoodEventAttendees, attendingEventAttendees] = partition(
      visibleAttendingEventAttendees,
      (eventAttendee) => winnerEventsById[eventAttendee.eventId]?.eventType === 'Food',
    );

    return {
      questionnaire: this.questionnaire,
      attendingEventAttendees,
      attendingFoodEventAttendees,
    };
  }

  @computed
  get questionnaireContactValidation(): ValidationState<QuestionnaireContactValidation> {
    return createValidationState(questionnaireContactValidationSchema.safeParse(this.getValidationInput()));
  }

  @computed
  get eventAttendeeValidations(): ValidationState<EventAttendeeValidation>[] {
    return this.questionnaire.eventAttendees.map((eventAttendee) => {
      if (!eventAttendee.isAttending) return VALID_VALIDATION_STATE;

      const winnerEvent = this.winnerEvents.find((event) => event.eventId === eventAttendee.eventId);
      if (!winnerEvent) return VALID_VALIDATION_STATE;

      const validationSchema = winnerEvent.eventType === 'Food' ? foodEventAttendeeValidationSchema : eventAttendeeValidationSchema;
      return createValidationState(validationSchema.safeParse(eventAttendee));
    });
  }

  @computed
  get questionnaireMediaValidation(): ValidationState<QuestionnaireMediaValidation> {
    return createValidationState(questionnaireMediaValidationSchema.safeParse(this.getValidationInput()));
  }

  @action
  setContact(contact: WinnerContact) {
    if (isEmpty(this.questionnaire.contact.contactName)) {
      this.questionnaire.contact.contactName = contact.contactName;
    }

    if (isEmpty(this.questionnaire.contact.companyWebsite)) {
      this.questionnaire.contact.companyWebsite = contact.companyWebsite;
    }
  }

  @action
  setQuestionnaireContactField<Key extends keyof WinnerContact>(updatedField: { key: Key; value: WinnerContact[Key] }): void {
    const { key, value } = updatedField;
    this.questionnaire.contact = { ...this.questionnaire.contact, [key]: value };
  }

  @action
  setAwardedNominations(awardedNominations: AwardedNomination[]): void {
    this.awardedNominations = awardedNominations;
  }

  @action
  setQuestionnaireProgress(questionnaireProgress: QuestionnaireProgress) {
    this.questionnaireProgress = copyQuestionnaireProgress(questionnaireProgress);
  }

  @action
  setWinnerEvents(winnerEvents: WinnerEvent[]): void {
    this.winnerEvents = !this.winnerRole || this.winnerRole === 'Both'
      ? winnerEvents
      : winnerEvents.filter(
        (winnerEvent) => winnerEvent.role === this.winnerRole || winnerEvent.role === 'Both',
      );

    if (this.winnerRole === 'Finalist') return;

    this.winnerEvents.forEach((winnerEvent) => {
      const hasEventAttendee = this.questionnaire.eventAttendees.some(
        (eventAttendee) => eventAttendee.eventId === winnerEvent.eventId,
      );
      if (!hasEventAttendee) {
        this.questionnaire.eventAttendees.push(
          blankEventAttendee(winnerEvent.eventId, this.questionnaireId),
        );
      }
    });
  }

  getEventAttendees(eventId: number): EventAttendee[] {
    return this.questionnaire.eventAttendees.filter(
      (eventAttendee) => eventAttendee.eventId === eventId,
    );
  }

  @action
  addEventAttendee(eventId: number, isAttending: boolean): void {
    const eventAttendee = blankEventAttendee(eventId, this.questionnaireId);
    eventAttendee.isAttending = isAttending;
    this.questionnaire.eventAttendees.push(eventAttendee);
  }

  @action
  removeEventAttendee(eventAttendee: EventAttendee): void {
    const eventAttendeeIndex = this.questionnaire.eventAttendees.indexOf(eventAttendee);
    if (eventAttendeeIndex < 0) return;

    if (eventAttendee.eventAttendeeId > 0) {
      this.deletedEventAttendeeIds.push(eventAttendee.eventAttendeeId);
    }
    this.questionnaire.eventAttendees.splice(eventAttendeeIndex, 1);
  }

  @action
  setEventAttendeeField<Key extends keyof EventAttendee>(eventAttendee: EventAttendee, updatedField: { key: Key; value: EventAttendee[Key] }): void {
    const { key, value } = updatedField;
    eventAttendee[key] = value;
  }

  @action
  setEventAttending(eventId: number, isAttending: boolean): void {
    this.getEventAttendees(eventId).forEach((eventAttendee) => {
      eventAttendee.isAttending = isAttending;
    });
  }

  getUploadedMedia(mediaType: QuestionnaireMediaType): UploadedMedia[] {
    return this.questionnaire.media.filter((uploadedMedia) =>
      mediaFileExtensions[mediaType].some((fileExtension) =>
        uploadedMedia.originalFileName.toLowerCase().endsWith(fileExtension)));
  }

  @action
  setWinnerId(winnerId: number): void {
    this.questionnaire.winnerId = winnerId;
  }

  @action
  setQuestionnaireId(questionnaireId: number): void {
    this.questionnaire.questionnaireId = questionnaireId;
    this.questionnaire.eventAttendees.forEach((eventAttendee) => {
      eventAttendee.questionnaireId = questionnaireId;
    });
  }

  @action
  setQuestionnaire(questionnaire: Questionnaire) {
    this.questionnaire = copyQuestionnaire(questionnaire);
  }

  @action
  setFurthestVisitedStepId(furthestVisitedStepId: QuestionnaireStepId): void {
    this.furthestVisitedStepId = furthestVisitedStepId;
  }

  @action
  setInitialStepId(initialStepId: QuestionnaireStepId): void {
    this.initialStepId = initialStepId;
  }

  @action
  markStepValidated(stepId: QuestionnaireStepId): void {
    this.validatedSteps = { ...this.validatedSteps, [stepId]: true };
  }

  isBeingValidated(stepId: QuestionnaireStepId): boolean {
    return Boolean(this.validatedSteps[stepId]);
  }

  isStepValid(stepId: QuestionnaireStepId): boolean {
    switch (stepId) {
      case 'contact':
        return this.questionnaireContactValidation.isValid;
      case 'event-attendees':
        return this.eventAttendeeValidations.every((validation) => validation.isValid);
      case 'logo':
        return true;
      case 'media':
        return this.questionnaireMediaValidation.isValid;
    }
  }

  @action
  setQuestionnaireField<Key extends keyof Questionnaire>(updatedField: { key: Key; value: Questionnaire[Key] }): void {
    const { key, value } = updatedField;
    this.questionnaire = { ...this.questionnaire, [key]: value };
  }

  @action
  resetStates(): void {
    this.furthestVisitedStepId = 'contact';
    this.initialStepId = 'contact';
    this.validatedSteps = {};
    this.questionnaire = blankQuestionnaire();
    this.winnerEvents = [];
    this.awardedNominations = [];
    this.questionnaireProgress = undefined;
    this.deletedEventAttendeeIds = [];
  }
}
